import type { Coding, Mother, Encounter, Immunization, ImmunizationRecommendation, MedicationRequest, PrescriptionTemplate, RxContent, VitalLimit } from '../types';
import { babies, doses, facilities, mothers, users } from './mockData';
import { defaultUnits, encounters, medicationRequests, rxTemplates, savedDiagnoses, vitalLimits } from './clinicalData';
import { STD_UNITS, type DefaultUnits } from './codes';
import { checkinResponses } from './clinicalData';
import { addAudit, type Actor } from './auditService';
import { respond } from './respond';
import * as real from './clinicalReal';
import { isReal, loadClinic, loadDailyStats } from './store';
import { scope } from './scope';

const patient = (id: string) => `Patient ${id}`;
const isDoctor = (a?: Actor) => a?.role === 'doctor';

export async function getVisits(motherId: string): Promise<Encounter[]> {
  if (isReal()) return real.getVisits(motherId);
  return respond(encounters.filter((e) => e.subjectId === motherId).sort((a, b) => b.date.localeCompare(a.date)));
}

export async function getVisit(id: string): Promise<Encounter | undefined> {
  if (isReal()) return real.getVisit(id);
  return respond(encounters.find((e) => e.id === id));
}

// Non-doctors may only record vitals; everything else is kept from the stored visit.
export async function saveVisit(input: Encounter, actor: Actor | undefined, sign: boolean): Promise<Encounter> {
  if (isReal()) return real.saveVisit(input, actor, sign);
  if (sign && !isDoctor(actor)) throw new Error('Only a doctor can sign a visit');
  const prev = encounters.find((e) => e.id === input.id);
  const base = prev ?? { ...input, symptoms: [], symptomNote: '', findings: '', ultrasound: '', conditions: [], testsOrdered: '', nextVisit: undefined, medicationRequestId: undefined };
  const next: Encounter = isDoctor(actor) ? { ...input } : { ...base, vitals: input.vitals, recordedBy: input.recordedBy, recordedAt: input.recordedAt };
  // A Hindi demo translation only stays while its English text is unchanged.
  (['symptomNote', 'findings', 'ultrasound'] as const).forEach((k) => { if (next[k] !== prev?.[k]) delete next[`${k}Hi`]; });
  if (sign) Object.assign(next, { status: 'finished', signedBy: actor?.name, signedAt: new Date().toISOString() });
  if (prev) Object.assign(prev, next);
  else encounters.push(next);
  if (next.medicationRequestId) {
    const rx = medicationRequests.find((r) => r.id === next.medicationRequestId);
    if (rx) rx.encounterId = next.id;
  }
  addAudit(actor, sign ? 'visit.signed' : 'visit.saved', patient(next.subjectId), next.facilityId, next.id);
  return respond(next);
}

/** Mothers with a visit recorded today (vitals taken or signed): they came in, so they are not "missed". */
export async function getVisitedToday(facilityId: string, todayIso: string): Promise<string[]> {
  if (isReal()) return real.getVisitedToday(facilityId, todayIso);
  return respond(Array.from(new Set(encounters.filter((e) => e.facilityId === facilityId && e.date === todayIso).map((e) => e.subjectId))));
}

// Visits where staff saved vitals and the doctor has not signed yet.
export async function getAwaitingDoctor(facilityId: string): Promise<{ visit: Encounter; mother: Mother }[]> {
  if (isReal()) return real.getAwaitingDoctor(facilityId);
  return respond(encounters
    .filter((e) => e.facilityId === facilityId && e.status === 'in-progress')
    .sort((a, b) => (a.recordedAt ?? a.date).localeCompare(b.recordedAt ?? b.date))
    .map((visit) => ({ visit, mother: mothers.find((m) => m.id === visit.subjectId)! })));
}

export async function getDefaultUnits(facilityId: string): Promise<DefaultUnits> {
  if (isReal()) return real.getDefaultUnits(facilityId);
  return respond({ ...(defaultUnits[facilityId] ?? STD_UNITS) });
}

export async function saveDefaultUnits(facilityId: string, units: DefaultUnits, actor?: Actor): Promise<void> {
  if (isReal()) return real.saveDefaultUnits(facilityId, units, actor);
  if (actor?.role !== 'doctor' && actor?.role !== 'facility_admin') throw new Error('Only doctors and facility admins can change units');
  defaultUnits[facilityId] = { ...units };
  addAudit(actor, 'units.saved', facilities.find((f) => f.id === facilityId)?.displayNameEn ?? facilityId, facilityId, Object.values(units).join(', '));
  return respond(undefined);
}

export async function getVitalLimits(facilityId: string): Promise<Record<string, VitalLimit>> {
  if (isReal()) return real.getVitalLimits(facilityId);
  return respond(JSON.parse(JSON.stringify(vitalLimits[facilityId] ?? {})));
}

export async function saveVitalLimits(facilityId: string, limits: Record<string, VitalLimit>, actor?: Actor): Promise<void> {
  if (isReal()) return real.saveVitalLimits(facilityId, limits, actor);
  if (!isDoctor(actor)) throw new Error('Only doctors can edit limits');
  vitalLimits[facilityId] = limits;
  addAudit(actor, 'limits.saved', facilities.find((f) => f.id === facilityId)?.displayNameEn ?? facilityId, facilityId);
  return respond(undefined);
}

export async function getSavedDiagnoses(userId: string) {
  return respond(savedDiagnoses[userId] ?? []);
}

export async function getTemplates(facilityId: string): Promise<PrescriptionTemplate[]> {
  if (isReal()) return real.getTemplates(facilityId);
  return respond(rxTemplates.filter((t) => t.facilityId === facilityId).map((t) => JSON.parse(JSON.stringify(t))));
}

export async function saveTemplate(tpl: PrescriptionTemplate, actor?: Actor): Promise<PrescriptionTemplate> {
  if (isReal()) return real.saveTemplate(tpl, actor);
  const saved = { ...tpl, id: tpl.id || `tpl-${Date.now()}`, updatedAt: new Date().toISOString() };
  const i = rxTemplates.findIndex((x) => x.id === saved.id);
  if (i >= 0) rxTemplates[i] = saved;
  else rxTemplates.push(saved);
  addAudit(actor, 'template.saved', saved.name, saved.facilityId);
  return respond(saved);
}

export async function duplicateTemplate(id: string, actor?: Actor): Promise<void> {
  if (isReal()) return real.duplicateTemplate(id, actor);
  const t = rxTemplates.find((x) => x.id === id);
  if (!t) return respond(undefined);
  rxTemplates.push({ ...JSON.parse(JSON.stringify(t)), id: `tpl-${Date.now()}`, name: `${t.name} (copy)`, archived: false, updatedAt: new Date().toISOString() });
  addAudit(actor, 'template.duplicated', t.name, t.facilityId);
  return respond(undefined);
}

export async function setTemplateArchived(id: string, archived: boolean, actor?: Actor): Promise<void> {
  if (isReal()) return real.setTemplateArchived(id, archived, actor);
  const t = rxTemplates.find((x) => x.id === id);
  if (t) t.archived = archived;
  addAudit(actor, archived ? 'template.archived' : 'template.restored', t?.name ?? id, t?.facilityId);
  return respond(undefined);
}

// Only prescriptions attached to a signed visit count as approved for the patient record.
export async function getPrescriptions(motherId: string): Promise<MedicationRequest[]> {
  if (isReal()) return real.getPrescriptions(motherId);
  const signed = (r: MedicationRequest) => encounters.some((e) => e.id === r.encounterId && e.status === 'finished');
  return respond(medicationRequests.filter((r) => r.subjectId === motherId && signed(r)).sort((a, b) => b.signedAt.localeCompare(a.signedAt)));
}

export async function getPrescription(id: string): Promise<MedicationRequest | undefined> {
  if (isReal()) return real.getPrescription(id);
  return respond(medicationRequests.find((r) => r.id === id));
}

// Registration number + council are required to sign; the signer's details are snapshotted for printing.
export async function signPrescription(facilityId: string, subjectId: string, rx: RxContent, templateId: string | undefined, actor?: Actor, diagnoses: Coding[] = []): Promise<MedicationRequest> {
  if (isReal()) return real.signPrescription(facilityId, subjectId, rx, templateId, actor, diagnoses);
  if (!isDoctor(actor)) throw new Error('Only a doctor can approve a prescription');
  const doc = users.find((u) => u.id === actor?.id)?.doctor;
  if (!doc?.regNo || !doc.council) throw new Error('NO_REG');
  if (!rx.medicines.length || rx.medicines.some((m) => !m.name.trim() || !m.dose.trim())) throw new Error('Every medicine needs a generic name and dose');
  const req: MedicationRequest = { resourceType: 'MedicationRequest', id: `rx-${Date.now()}`, facilityId, subjectId, templateId, status: 'active', signedBy: actor?.name ?? '', signedAt: new Date().toISOString(), signer: { name: actor?.name ?? '', ...doc }, diagnoses, ...JSON.parse(JSON.stringify(rx)) };
  medicationRequests.push(req);
  addAudit(actor, 'prescription.signed', patient(subjectId), facilityId, req.id);
  return respond(req);
}

export async function getImmunizations(motherId: string): Promise<{ done: Immunization[]; due: ImmunizationRecommendation[] }> {
  const c = isReal() ? await loadClinic(scope().facilityId) : { babies, doses };
  const baby = c.babies.find((b) => b.motherId === motherId);
  const list = c.doses.filter((d) => d.babyId === baby?.id && d.code);
  return respond({
    done: list.filter((d) => d.status === 'attended').map((d) => ({ resourceType: 'Immunization', id: d.id, subjectId: motherId, vaccineCode: d.code!, occurrence: d.dueDate, status: 'completed' })),
    due: list.filter((d) => d.status !== 'attended').map((d) => ({ resourceType: 'ImmunizationRecommendation', id: `rec-${d.id}`, subjectId: motherId, vaccineCode: d.code!, dueDate: d.dueDate })),
  });
}

// Counts only for super admin.
export async function getClinicalCounts(facilityId: string): Promise<{ checkins: number; redFlags: number; visits: number }> {
  if (isReal()) {
    const rows = await loadDailyStats(facilityId, 90);
    const total = (k: 'checkins' | 'redFlags' | 'visits') => rows.reduce((n, r) => n + (r[k] ?? 0), 0);
    return { checkins: total('checkins'), redFlags: total('redFlags'), visits: total('visits') };
  }
  const res = checkinResponses.filter((r) => r.facilityId === facilityId);
  return respond({ checkins: res.length, redFlags: res.reduce((n, r) => n + r.answers.filter((a) => a.redFlag).length, 0), visits: encounters.filter((e) => e.facilityId === facilityId).length });
}
